"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export function measureCssSizePx(widthCss: string, heightCss: string) {
  if (typeof document === "undefined") {
    return null;
  }

  const probe = document.createElement("div");
  probe.style.cssText = [
    "position: absolute",
    "visibility: hidden",
    "pointer-events: none",
    `width: ${widthCss}`,
    `height: ${heightCss}`,
  ].join(";");
  document.body.appendChild(probe);
  const widthPx = probe.offsetWidth;
  const heightPx = probe.offsetHeight;
  probe.remove();

  if (widthPx <= 0 || heightPx <= 0) {
    return null;
  }

  return { widthPx, heightPx };
}

/** Fit page into available width without upscaling past 1:1. */
export function computePreviewPageScale(
  availableWidthPx: number,
  pageWidthPx: number,
) {
  if (pageWidthPx <= 0 || availableWidthPx <= 0) {
    return 1;
  }

  return Math.min(1, availableWidthPx / pageWidthPx);
}

/**
 * Observe a full-width measure host (not the scaled layout box). Scaling the
 * layout box itself would make clientWidth equal page*scale and freeze resize.
 */
export function usePreviewPageScale(pageWidthCss: string, enabled: boolean) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [metrics, setMetrics] = useState<{
    scale: number;
    widthPx: number;
  } | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;

    if (!container || !enabled) {
      return;
    }

    const pageSize = measureCssSizePx(pageWidthCss, pageWidthCss);

    if (!pageSize) {
      return;
    }

    const availableWidth = container.clientWidth;
    const scale = computePreviewPageScale(availableWidth, pageSize.widthPx);
    const widthPx = pageSize.widthPx;

    setMetrics((previous) => {
      if (
        previous &&
        previous.scale === scale &&
        previous.widthPx === widthPx
      ) {
        return previous;
      }

      return { scale, widthPx };
    });
  }, [enabled, pageWidthCss]);

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;

      if (node && enabled) {
        measure();
      }
    },
    [enabled, measure],
  );

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !enabled || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      measure();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [enabled, measure]);

  return { containerRef: setContainerRef, metrics };
}
